import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { useContractReviews, useContractReviewRealtime } from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { formatDate } from '@/lib/utils'
import { explainError } from '@/lib/explain-error'
import { NewContractReviewModal } from '@/components/contract-reviews/NewContractReviewModal'
import type { ContractReviewListItem } from '@/types'

/**
 * The review queue.
 *
 * Sorted so the question "what is waiting on me" is answered without reading: the WITH YOU
 * column states whose court the ball is in, in words, for every row.
 *
 * Open reviews only by default. The closed ones are the archive, and a queue that fills with
 * signed contracts stops functioning as a queue.
 */

const PAGE_SIZE = 25

/** Said in words, never by colour alone. */
function whoseCourt(review: ContractReviewListItem, myId?: string): string {
  if (!review.waiting_on) return '—'
  const waitingId = review.waiting_on === 'reviewer' ? review.reviewer_id : review.requested_by_id
  if (myId && waitingId === myId) return 'You'
  const name = review.waiting_on === 'reviewer' ? review.reviewer_name : review.requested_by_name
  return name || (review.waiting_on === 'reviewer' ? 'Reviewer' : 'Requester')
}

export default function ContractReviews() {
  useDocumentTitle('Contract reviews')
  useContractReviewRealtime()

  const [searchParams, setSearchParams] = useSearchParams()
  const { currentUser, impersonatedUser } = useUser()
  const me = impersonatedUser?.id || currentUser?.id

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1)
  const includeClosed = searchParams.get('closed') === 'true'
  // The old behaviour, kept one click away now that the list shows everyone's by default.
  const mineOnly = searchParams.get('mine') === 'true'

  const { data, isLoading, error } = useContractReviews({
    page,
    limit: PAGE_SIZE,
    include_closed: includeClosed,
    ...(mineOnly ? { mine: true } : {}),
  })

  const reviews = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const setParam = (key: string, value: string | null, resetPage = true) => {
    const next = new URLSearchParams(searchParams)
    if (value === null) next.delete(key)
    else next.set(key, value)
    if (resetPage) next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const [raising, setRaising] = useState(false)

  /*
   * From the server, across the whole result, not counted from this page.
   *
   * It used to filter the 25 rows already on screen and print that against `total`, so on page
   * two it would announce "nothing waiting on you" while the thing waiting on you sat on page
   * one. The two numbers were measuring different populations.
   */
  const waitingOnMe = data?.waiting_on_you ?? 0

  return (
    <main className="p-6 space-y-4">
      <section aria-labelledby="contract-reviews-heading">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 id="contract-reviews-heading" className="text-2xl font-semibold">
              Contract reviews
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every contract in review across the team, and where each one has got to.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={mineOnly}
                onChange={(e) => setParam('mine', e.target.checked ? 'true' : null)}
                className="h-4 w-4"
              />
              Only mine
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeClosed}
                onChange={(e) => setParam('closed', e.target.checked ? 'true' : null)}
                className="h-4 w-4"
              />
              Include finished reviews
            </label>
            <Button onClick={() => setRaising(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Send for review
            </Button>
          </div>
        </div>

        <NewContractReviewModal open={raising} onOpenChange={setRaising} />

        {isLoading && (
          <p className="text-sm text-muted-foreground mt-4" role="status" aria-live="polite">
            Loading contract reviews…
          </p>
        )}

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              {explainError(error, 'The contract reviews could not be loaded')}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !error && reviews.length === 0 && (
          <Card className="mt-4">
            <CardContent className="py-6">
              <p className="text-sm text-muted-foreground">
                {includeClosed
                  ? 'No contract reviews yet.'
                  : mineOnly
                    ? 'Nothing of yours in progress. Untick "Only mine" to see the team\'s.'
                    : 'Nothing in progress. Tick "Include finished reviews" to see the archive.'}
              </p>
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && reviews.length > 0 && (
          <>
            {/* A plain count rather than a badge: it is the first thing worth knowing and
                should not depend on noticing a colour. */}
            <p className="text-sm mt-4" role="status">
              {waitingOnMe > 0
                ? `${waitingOnMe} of ${total} waiting on you.`
                : `Nothing waiting on you. ${total} in total.`}
            </p>

            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Contract reviews, most recently updated first, showing whose turn each one is
                </caption>
                <thead>
                  <tr className="border-b text-left">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Contract
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Customer
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Status
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      With
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Comments
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => {
                    const withWhom = whoseCourt(review, me)
                    return (
                      <tr key={review.id} className="border-b last:border-0">
                        <th scope="row" className="py-2 pr-4 font-normal text-left">
                          <Link
                            to={`/contract-reviews/${review.id}`}
                            className="text-primary underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {review.title}
                          </Link>
                          <span className="block text-xs text-muted-foreground">
                            CR-{review.review_number}
                          </span>
                        </th>
                        <td className="py-2 pr-4">
                          <Link
                            to={`/accounts/${review.account_id}`}
                            className="text-primary underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {review.account_name || review.account_id}
                          </Link>
                        </td>
                        <td className="py-2 pr-4">{review.status}</td>
                        <td className="py-2 pr-4">
                          <span className={withWhom === 'You' ? 'font-semibold' : undefined}>
                            {withWhom}
                          </span>
                        </td>
                        <td className="py-2 pr-4 tabular-nums">{review.comment_count}</td>
                        <td className="py-2">{formatDate(review.last_modified_date)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} · {total} reviews
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setParam('page', String(page - 1), false)}
                  aria-label="Go to the previous page of contract reviews"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setParam('page', String(page + 1), false)}
                  aria-label="Go to the next page of contract reviews"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </section>
    </main>
  )
}
